/* eslint-disable react/jsx-no-bind */

import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import noop from 'lodash/noop';

import timeBetween from 'src/common/utils/time-between';
import { localiseUrlII } from 'src/common/utils/format-localised-url';
import { replaceTextWithValues } from 'src/common/utils/text-replace';

import { useCountry, useGlobalContent } from '../../../hooks';
import { trackViewOrderClick, trackEditOrderClick } from '../../../../analytics/category/account';
import { allowEdit, buildEditUrl } from '../../CartItems/LineItem/helpers';

import OrderButton from './OrderButton';

import './OrderButtons.scss';

const ButtonsContext = React.createContext({
  viewVisible: false,
  setViewVisible: noop,
  editVisible: false,
  setEditVisible: noop,
  trackVisible: false,
  setTrackVisible: noop,
  helpVisible: false,
  setHelpVisible: noop,
});

const OrderButtons = ({ children }) => {
  const [ viewVisible, setViewVisible ] = useState(false);
  const [ editVisible, setEditVisible ] = useState(false);
  const [ trackVisible, setTrackVisible ] = useState(false);
  const [ helpVisible, setHelpVisible ] = useState(false);

  const context = {
    viewVisible,
    setViewVisible,
    editVisible,
    setEditVisible,
    trackVisible,
    setTrackVisible,
    helpVisible,
    setHelpVisible,
  };

  const visibleButtons = [viewVisible, editVisible, trackVisible, helpVisible].filter(value => value);
  const count = visibleButtons.length;

  return (
    <ButtonsContext.Provider value={context}>
      <div className={cx('order-buttons', {
        [`order-buttons--${count}-visible`]: count,
      })}>
        <div className="order-buttons__wrapper">
          {children}
        </div>
      </div>
    </ButtonsContext.Provider>
  );
};

OrderButtons.View = ({ number, state, label, url }) => {
  const href = `/account/orders/${number}`;
  const country = useCountry();
  const { buttons } = useGlobalContent('orders');
  const { label: buttonLabel } = buttons.find(button => button.type === 'view');

  const { setViewVisible } = useContext(ButtonsContext);
  useEffect(() => {
    setViewVisible(true);
  }, []);

  return (
    <OrderButton
      type="view"
      label={label || buttonLabel}
      href={url || localiseUrlII(country, href)}
      handleOnClick={() => trackViewOrderClick(state)}
      raised
    />
  );
};

OrderButtons.Edit = ({
  type,
  number,
  state,
  processAt,
  id,
  productSku,
  formatIdentifier,
  customisation,
  components,
  label,
  url
}) => {
  const country = useCountry();
  const { buttons, timeLeftLabel } = useGlobalContent('orders');
  const { label: buttonLabel } = buttons.find(button => button.type === 'edit');

  const timeRemaining = timeBetween(processAt);
  const displayTime = (timeRemaining.hours || '< 1').toString();

  if (!allowEdit({ productSku, type, state }) || timeRemaining.minutes <= 0) {
    return null;
  }

  const timeLeft = replaceTextWithValues(timeLeftLabel, { time_to_print: displayTime });

  let href = `/account/orders/${number}`;

  if (id && productSku && customisation && formatIdentifier) {
    const item = { id, productSku, customisation, components, formatIdentifier };
    href = buildEditUrl({ item, number, country });
  }

  const { setEditVisible } = useContext(ButtonsContext);
  useEffect(() => {
    setEditVisible(true);
  }, []);

  return (
    <OrderButton
      type="edit"
      label={label || replaceTextWithValues(buttonLabel, { time_left: timeLeft })}
      href={url || localiseUrlII(country, href)}
      handleOnClick={() => trackEditOrderClick(displayTime)}
      outlined
    />
  );
};

OrderButtons.Track = ({ state, label, url, isPrimary }) => {
  if (state !== 'shipped') {
    return null;
  }

  const { buttons } = useGlobalContent('orders');
  const { label: buttonLabel } = buttons.find(button => button.type === 'track');

  const { setTrackVisible } = useContext(ButtonsContext);
  useEffect(() => {
    setTrackVisible(true);
  }, []);

  return (
    <OrderButton
      type="track"
      label={label || buttonLabel}
      href={url}
      outlined={!isPrimary}
      raised={isPrimary}
    />
  );
};

OrderButtons.Help = ({ url, label }) => {
  const { buttons } = useGlobalContent('orders');
  const { label: buttonLabel, href } = buttons.find(button => button.type === 'help');
  const { setHelpVisible } = useContext(ButtonsContext);
  useEffect(() => {
    setHelpVisible(true);
  }, []);

  return (
    <OrderButton
      type="help"
      label={label || buttonLabel}
      href={url || href}
      outlined
    />
  );
};

OrderButtons.View.propTypes = {
  number: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  label: PropTypes.string,
  url: PropTypes.string,
};

OrderButtons.Edit.propTypes = {
  type: PropTypes.string,
  number: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
  processAt: PropTypes.string.isRequired,
  timeLeftLabel: PropTypes.string.isRequired,
  label: PropTypes.string,
  url: PropTypes.string,
  productSku: PropTypes.string,
  formatIdentifier: PropTypes.string,
  id: PropTypes.number,
  country: PropTypes.object.isRequired,
  customisation: PropTypes.object,
  components: PropTypes.array,
};

OrderButtons.Track.propTypes = {
  state: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  label: PropTypes.string,
  isPrimary: PropTypes.bool,
};

OrderButtons.Help.propTypes = {
  url: PropTypes.string,
  label: PropTypes.string,
};

OrderButtons.propTypes = {
  children: PropTypes.node.isRequired,
};

export default OrderButtons;



// WEBPACK FOOTER //
// ./src/client/js/view/components/Order/OrderButtons/OrderButtons.js